/**
 * The service enquiry form — the only conversion point for the $299/mo offer.
 *
 * Deliberately NOT a checkout. Roadmap v3.0: the deliverable, the exact price
 * shape and the fulfilment cost are all unproven, so the first customers are
 * invoiced by hand. Building `mode: 'subscription'` before anyone has paid
 * means guessing the schema, and there is no recurring Stripe Price to point a
 * Session at anyway.
 *
 * It asks for their domain AND a competitor because that pair is the entire
 * qualification step: it is what `npm run gap` needs to produce a real answer
 * before anyone gets on a call. It also self-selects — someone who cannot name
 * a competitor is not ready to buy link building.
 *
 * Nothing is persisted. There is no lead model for this yet and inventing one
 * for a form with no customers would be the same mistake as building billing
 * early; the enquiry goes to the fulfilment inbox, same as foundation-pack
 * orders. If volume ever justifies a table, that is the moment to add it.
 *
 * It IS instrumented, which is a different question from persistence and was
 * missed when this shipped. This is the primary offer's only conversion point
 * and every priced CTA on the site now points at it, so an untracked submission
 * means paid search cannot bid toward the one thing worth buying. The Ads
 * conversion rides a one-shot cookie claimed in `root.tsx`
 * (`enquiry-conversion.ts` explains why not the URL); PostHog gets the ATTEMPT
 * and the SUCCESS as separate events so a silent Turnstile rejection is visible
 * as a gap rather than as nothing at all.
 */
import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useEffect, useRef } from 'react'
import { data, Form, redirect, useLoaderData } from 'react-router'
import { z } from 'zod'
import { trackClient } from '#app/analytics/client.ts'
import { enquiryConversionCookie } from '#app/analytics/enquiry-conversion.ts'
import { Eyebrow, Heading } from '#app/components/brand/section.tsx'
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx'
import { TurnstileWidget } from '#app/components/turnstile.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { verifyTurnstile } from '#app/utils/abuse.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { getClientIp } from '#app/utils/scan-request.server.ts'
import { buildMeta } from '#app/utils/seo.ts'
import { type Route } from './+types/get-started.ts'
import { Footer } from './components/footer.tsx'
import Header from './components/header.tsx'

export const meta: Route.MetaFunction = () =>
	buildMeta({
		title: 'Get started | Competitor Backlinks',
		description:
			'Tell us your site and one competitor. We reply with the links they have that you do not, and what we would go after first.',
		path: '/get-started',
	})

const EnquirySchema = z.object({
	name: z.string().min(1, 'Tell us your name').max(120),
	email: z.string().email('Enter a valid email').max(200),
	domain: z.string().min(3, 'Enter your website').max(253),
	competitor: z.string().min(3, 'Name one competitor').max(253),
	notes: z.string().max(2000).optional(),
})

export async function loader() {
	return data({ siteKey: process.env.TURNSTILE_SITE_KEY ?? null })
}

/** Visitor-supplied strings land in an HTML email we send to ourselves. */
function escapeHtml(value: string) {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()

	// Turnstile BEFORE parsing, matching the checker's order: a bot should not
	// get validation feedback, and this route sends mail.
	const passed = await verifyTurnstile(
		formData.get('cf-turnstile-response') as string | null,
		getClientIp(request) ?? undefined,
	)
	if (!passed) {
		return data(
			{ result: null, formError: 'Verification failed. Please try again.' },
			{ status: 400 },
		)
	}

	const submission = parseWithZod(formData, { schema: EnquirySchema })
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply(), formError: null },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { name, email, domain, competitor, notes } = submission.value
	const to =
		process.env.FULFILLMENT_NOTIFICATION_EMAIL ?? 'info@competitorbacklinks.com'

	// Reference shared by the notification, the Ads conversion's dedupe key and
	// the PostHog success event. With no lead table, this is the only way to tie
	// a conversion in the Ads UI back to a specific enquiry in the inbox — and
	// it is what lets the same visitor enquire about a second site and count as
	// a second lead rather than being deduped away.
	const enquiryId = crypto.randomUUID()

	// Best-effort: a delivery failure must not lose the enquiry silently, so it
	// is logged loudly and the visitor still gets a confirmation. Losing the
	// lead to a mail hiccup is worse than a duplicate follow-up.
	const lines = [
		`Name: ${name}`,
		`Email: ${email}`,
		`Their site: ${domain}`,
		`Competitor: ${competitor}`,
		notes ? `Notes: ${notes}` : null,
		'',
		`Run: npm run gap -- ${domain} ${competitor}`,
		`Ref: ${enquiryId}`,
	].filter(Boolean) as string[]

	const sent = await sendEmail({
		to,
		subject: `Service enquiry: ${domain} vs ${competitor}`,
		text: lines.join('\n'),
		html: lines.map((l) => `<p>${escapeHtml(l)}</p>`).join(''),
	}).catch((error: unknown) => {
		console.error('[get-started] enquiry email failed to send', error)
		return null
	})
	if (!sent) {
		console.error(
			`[get-started] UNSENT ENQUIRY ref=${enquiryId} name=${name} email=${email} domain=${domain} competitor=${competitor}`,
		)
	}

	// A dedicated thanks route rather than a redirect back here. The
	// POST-redirect-GET was always what stopped a refresh resubmitting, but
	// redirecting to the SAME route re-renders this component instead of
	// remounting it, so the uncontrolled inputs keep their values: the visitor
	// got a toast over a form still full of their details, which reads as "did
	// that send?". Landing somewhere else unmounts the form, and gives the
	// conversion a real moment to fire on.
	//
	// The conversion still rides the marker cookie, not the URL — the thanks
	// page is shareable and back-navigable. See `enquiry-conversion.ts`.
	return redirect('/get-started/thanks', {
		headers: { 'set-cookie': enquiryConversionCookie(enquiryId) },
	})
}

export default function GetStartedPage({ actionData }: Route.ComponentProps) {
	const { siteKey } = useLoaderData<typeof loader>()
	const isPending = useIsPending()
	const [form, fields] = useForm({
		id: 'service-enquiry',
		// Without this the server's reply is never shown. Client-side validation
		// hides most of it, but a Turnstile rejection has no client equivalent:
		// the action returned `formError`, nothing rendered it, and the visitor
		// watched the button do nothing.
		lastResult: actionData?.result ?? null,
		constraint: getZodConstraint(EnquirySchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: EnquirySchema })
		},
		shouldRevalidate: 'onBlur',
	})

	// The ATTEMPT event. Fired off the navigation rather than an onSubmit
	// handler for two reasons: `getFormProps` already owns onSubmit (Conform
	// cancels the submit when client validation fails, and clobbering that
	// handler would count attempts that never left the browser), and a POST
	// navigation starting is the exact moment we know one did. The ref makes it
	// once per submission, not once per render while pending.
	const trackedSubmit = useRef(false)
	useEffect(() => {
		if (!isPending) {
			trackedSubmit.current = false
			return
		}
		if (trackedSubmit.current) return
		trackedSubmit.current = true
		trackClient('enquiry_submitted', { route_id: '/get-started' })
	}, [isPending])

	return (
		<div className="bg-canvas text-ink min-h-screen font-sans">
			<Header />
			<main className="mx-auto max-w-[720px] px-8 pt-16 pb-24">
				<div className="text-center">
					<Eyebrow className="mb-3.5">Get started</Eyebrow>
					<Heading className="mb-3 text-[clamp(28px,4.4vw,44px)] tracking-[-0.02em]">
						Tell us who you are up against.
					</Heading>
					<p className="mx-auto max-w-[520px] text-lg text-pretty text-[#57565E]">
						Give us your site and one competitor. We reply with the sites
						linking to them and not to you, and what we would go after first. No
						charge for that, and no call unless you want one.
					</p>
				</div>

				<Form
					method="post"
					{...getFormProps(form)}
					className="border-hairline mt-10 rounded-[24px] border bg-white p-6 sm:p-8"
				>
					<div className="grid gap-x-5 sm:grid-cols-2">
						<Field
							labelProps={{ children: 'Your name' }}
							inputProps={{
								...getInputProps(fields.name, { type: 'text' }),
								autoComplete: 'name',
							}}
							errors={fields.name.errors}
						/>
						<Field
							labelProps={{ children: 'Email' }}
							inputProps={{
								...getInputProps(fields.email, { type: 'email' }),
								autoComplete: 'email',
							}}
							errors={fields.email.errors}
						/>
						<Field
							labelProps={{ children: 'Your website' }}
							inputProps={{
								...getInputProps(fields.domain, { type: 'text' }),
								placeholder: 'yoursite.com',
							}}
							errors={fields.domain.errors}
						/>
						<Field
							labelProps={{ children: 'A competitor' }}
							inputProps={{
								...getInputProps(fields.competitor, { type: 'text' }),
								placeholder: 'competitor.com',
							}}
							errors={fields.competitor.errors}
						/>
					</div>

					<TextareaField
						labelProps={{ children: 'Anything else? (optional)' }}
						textareaProps={{
							...getInputProps(fields.notes, { type: 'text' }),
							rows: 3,
							placeholder:
								'What you have already tried, or what you are trying to rank for.',
						}}
						errors={fields.notes.errors}
					/>

					{siteKey ? <TurnstileWidget siteKey={siteKey} /> : null}
					<ErrorList
						id={form.errorId}
						errors={[
							...(form.errors ?? []),
							...(actionData?.formError ? [actionData.formError] : []),
						]}
					/>

					<StatusButton
						type="submit"
						size="lg"
						className="mt-4 w-full sm:w-auto"
						status={isPending ? 'pending' : 'idle'}
						disabled={isPending}
					>
						{isPending ? 'Sending' : 'Send it over'}
						{isPending ? null : <Icon name="arrow-right" className="size-4" />}
					</StatusButton>

					<p className="mt-4 text-[13px] text-[#57565E]">
						The service is $299 a month and you can cancel any time. Sending
						this does not sign you up for anything.
					</p>
				</Form>
			</main>
			<Footer />
		</div>
	)
}
